import { computed, inject, Injectable, linkedSignal, signal, Signal, WritableSignal } from '@angular/core';
import { LlmConversationNode } from '../../classes';
import { ELlmResponseType } from '../../enums';
import { ARTIFACT_TYPES } from '../../tokens';
import { AidaService } from '../aida';
import { LlmForkService } from '../llm-fork';

@Injectable()
export class LlmArtifactService {

	protected readonly aida: AidaService = inject(AidaService);

	protected readonly forks: LlmForkService = inject(LlmForkService);

	protected readonly artifactTypes: Set<ELlmResponseType> = inject(ARTIFACT_TYPES);

	public readonly isOpen: WritableSignal<boolean> = signal(false);

	/** The list of nodes considered to be artifacts from the current conversation fork */
	public readonly list: Signal<LlmConversationNode[]> = computed(() => {
		return this.generateArtifactList(this.forks.activeFork());
	});

	/** The number of artifacts in the current fork */
	public readonly count: Signal<int> = computed(() => this.list().length);

	/**
	 * Internal Linked Signal to track current artifact.
	 * Not exposed as `public` because we want to enforce setting the value
	 * through the `setActiveArtifact` method for `responseType` checking
	 */
	protected readonly currentArtifact: WritableSignal<LlmConversationNode | null> = linkedSignal(() => {
		return this.list()[0] || null;
	});

	/** Public accessor signal for `currentArtifact` */
	public readonly activeArtifact: Signal<LlmConversationNode | null> = this.currentArtifact.asReadonly();

	public open(_node: LlmConversationNode): void {
		this.isOpen.set(true);
		this.setActiveArtifact(_node);
	}

	public setActiveArtifact(_node: LlmConversationNode): void {
		if (!this.artifactTypes.has(_node.responseType))
			throw new TypeError(`${_node.responseType} is not considered an artifact`);

		this.currentArtifact.set(_node);
	}

	public generateArtifactList(_node: LlmConversationNode): LlmConversationNode[] {
		const _artifacts: LlmConversationNode[] = [];

		while (_node != null) {
			if (this.artifactTypes.has(_node.responseType))
				_artifacts.push(_node);

			_node = _node.parent;
		}

		return _artifacts;
	}

}
